import { useCallback, useEffect, useRef } from 'react';
import { useGameSession } from './GameSession';

const roundKey = (round) => `${round.game || 'keno'}:${round.request_id}`;

// Server settlement is not the same event as a visible outcome. Keep completed
// rounds pending until the board reveals them; all stats share that one event.
export const useRevealedStats = (owner, scope) => {
  const { addRound } = useGameSession();
  const pending = useRef(new Map()), mounted = useRef(false);
  const active = useRef({ owner, scope });
  active.current = { owner, scope };
  useEffect(() => {
    mounted.current = true;
    const queue = pending.current;
    return () => {
      mounted.current = false;
      // Leaving removes the animation, not the settled bet. Preserve ownership
      // and release any Auto loop waiting on presentation so it can stop.
      queue.forEach((item) => { addRound(item.round, item.owner); item.resolve(); });
      queue.clear();
    };
  }, [owner, scope, addRound]);
  const stageRound = useCallback((round) => {
    if (!round?.request_id || round.finished === false) return Promise.resolve();
    if (!mounted.current || active.current.owner !== owner || active.current.scope !== scope) {
      addRound(round, owner); return Promise.resolve();
    }
    const id = roundKey(round), previous = pending.current.get(id);
    if (previous) return previous.promise;
    let resolve;
    const promise = new Promise((done) => { resolve = done; });
    pending.current.set(id, { round, owner, promise, resolve });
    return promise;
  }, [owner, scope, addRound]);
  const revealRound = useCallback((round) => {
    if (!round?.request_id) return;
    const id = roundKey(round), item = pending.current.get(id);
    if (!item) return;
    pending.current.delete(id);
    addRound(item.round, item.owner);
    item.resolve();
  }, [addRound]);
  return { stageRound, revealRound };
};